/**
 * Utilities for working with Google Tag Manager dataLayer
 */

import type { FlatMappedGtmEventPayload, MappedContentData, MappedGtmEventName } from './core/gtm.types';
import type { UserDto } from '@/queries/user/user.schema';
import { parseCookies } from 'nookies';
import { Env } from '@/consts/Env';
import { readCustomerSourceHeadersFromStorage } from '@/libs/userSource/customerSourceStorage';
import { sanitizeTrackingValue } from '@/libs/userSource/trackingValue';
import { DEFAULT_EVENT_CONTENT, EVENTS_WITHOUT_DEFAULT_CONTENT } from './core/constants';

const BROWSER_ID_STORAGE_KEY = 'gtm_browser_id_v1';

type PushCustomGtmEventOptions = {
  eventName: MappedGtmEventName;
  user?: Partial<UserDto>;
  auth_source?: string;
};

function resolveDefaultContent(eventName: MappedGtmEventName): MappedContentData {
  if (eventName in DEFAULT_EVENT_CONTENT) {
    return DEFAULT_EVENT_CONTENT[eventName as keyof typeof DEFAULT_EVENT_CONTENT];
  }

  if (EVENTS_WITHOUT_DEFAULT_CONTENT.includes(eventName as (typeof EVENTS_WITHOUT_DEFAULT_CONTENT)[number])) {
    return {};
  }

  return {};
}

function hasDataLayerAccess(): boolean {
  return isClientSide() && !!Env.NEXT_PUBLIC_GTM_ID;
}

function isClientSide(): boolean {
  return typeof window !== 'undefined';
}

function sanitizeEventPayload<T extends Record<string, unknown>>(payload: T): T {
  const sanitizedEntries = Object.entries(payload).filter(([key, value]) => {
    if (key === 'event' || key === 'event_id' || key === 'event_time') {
      return true;
    }

    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return true;
  });

  return Object.fromEntries(sanitizedEntries) as T;
}

function buildEventId(eventName: string): string {
  const randomSuffix = Math.random().toString(36).slice(2, 10);
  return `${eventName}_${Date.now()}_${randomSuffix}`;
}

function extractStringField(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function resolveTrackingId(candidates: Array<string | null | undefined>): string | undefined {
  for (const candidate of candidates) {
    const sanitized = sanitizeTrackingValue(candidate);
    if (sanitized) {
      return sanitized;
    }
  }

  return undefined;
}

function resolveAttributionFields(): Partial<FlatMappedGtmEventPayload> {
  if (!isClientSide()) {
    return {};
  }

  const cookies = parseCookies();
  const searchParams = new URL(window.location.href).searchParams;
  const storedHeaders = readCustomerSourceHeadersFromStorage();

  return sanitizeEventPayload({
    ttclid: resolveTrackingId([cookies.ttclid, cookies._ttclid, storedHeaders['x-ttclid'], searchParams.get('ttclid')]),
    aclid: resolveTrackingId([cookies.aclid, cookies._aclid, searchParams.get('aclid')]),
    clickid: resolveTrackingId([cookies.click_id, cookies.clickid, storedHeaders['x-click-id'], searchParams.get('click_id'), searchParams.get('clickid')]),
    gclid: resolveTrackingId([cookies.gclid, cookies._gclid, storedHeaders['x-gclid'], searchParams.get('gclid')]),
    fbc: resolveTrackingId([cookies._fbc, cookies.fbc, storedHeaders['x-fbc'], searchParams.get('fbc')]),
  });
}

function generateBrowserId(): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `browser_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function resolveBrowserId(): string | undefined {
  if (!isClientSide()) {
    return undefined;
  }

  let cachedBrowserId: string | undefined;

  try {
    cachedBrowserId = extractStringField(window.localStorage.getItem(BROWSER_ID_STORAGE_KEY));
  } catch {
    // Ignore storage errors.
  }

  const resolvedBrowserId = cachedBrowserId ?? generateBrowserId();

  try {
    window.localStorage.setItem(BROWSER_ID_STORAGE_KEY, resolvedBrowserId);
  } catch {
    // Ignore storage errors.
  }

  return resolvedBrowserId;
}

function pushEvent(payload: FlatMappedGtmEventPayload): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

function normalizeCustomGtmEventArgs(
  eventOrOptions: MappedGtmEventName | PushCustomGtmEventOptions,
  userArg?: Partial<UserDto>,
): PushCustomGtmEventOptions {
  if (typeof eventOrOptions === 'string') {
    return {
      eventName: eventOrOptions,
      user: userArg,
    };
  }

  return eventOrOptions;
}

export type { PushCustomGtmEventOptions };

export {
  buildEventId,
  extractStringField,
  hasDataLayerAccess,
  isClientSide,
  normalizeCustomGtmEventArgs,
  pushEvent,
  resolveAttributionFields,
  resolveBrowserId,
  resolveDefaultContent,
  sanitizeEventPayload,
};
